import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "fileInput",
    "emptyState",
    "previewWrapper",
    "previewImage",
    "removeCheckbox",
    "metaRow",
    "dropzone"
  ]

  connect() {
    const initialSrc = this.previewImageTarget?.getAttribute('src')
    if (initialSrc) {
      this.showPreview(initialSrc)
      this.setRemoveCheckbox(true)
    } else {
      this.showEmptyState()
    }
  }

  dragEnter(event) {
    event.preventDefault()
    if (this.isDisabled()) return
    this.dropzoneTarget.classList.add('dz-drag-hover')
  }

  dragOver(event) {
    event.preventDefault()
    if (this.isDisabled()) return
    this.dropzoneTarget.classList.add('dz-drag-hover')
  }

  dragLeave(event) {
    event.preventDefault()
    if (!this.dropzoneTarget.contains(event.relatedTarget)) {
      this.dropzoneTarget.classList.remove('dz-drag-hover')
    }
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('dz-drag-hover')
    if (this.isDisabled()) return
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      this.assignFile(files[0])
    }
  }

  fileChanged(event) {
    const files = event.target.files
    if (files && files.length > 0) {
      this.renderPreview(files[0])
      this.uncheckRemove()
      this.setRemoveCheckbox(true)
    } else {
      this.showEmptyState()
    }
  }

  removeToggled(event) {
    if (event.target.checked) {
      this.clearFile()
      this.showEmptyState()
    }
  }

  assignFile(file) {
    if (!file || !this.hasFileInputTarget) return

    if (typeof DataTransfer !== 'undefined') {
      try {
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(file)
        this.fileInputTarget.files = dataTransfer.files
      } catch (error) {
        // Fallback: clear input so user can re-upload via click.
        this.fileInputTarget.value = ''
      }
    }
    this.renderPreview(file)
    this.uncheckRemove()
    this.setRemoveCheckbox(true)
  }

  renderPreview(file) {
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      this.showPreview(event.target?.result)
    }
    reader.readAsDataURL(file)
  }

  showPreview(url) {
    if (!url) {
      this.showEmptyState()
      return
    }

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url
    }
    if (this.hasPreviewWrapperTarget) {
      this.previewWrapperTarget.classList.remove('hidden')
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('hidden')
    }
    this.disableInput()
  }

  showEmptyState() {
    if (this.hasPreviewWrapperTarget) {
      this.previewWrapperTarget.classList.add('hidden')
    }
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.removeAttribute('src')
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove('hidden')
    }
    this.setRemoveCheckbox(false)
    this.enableInput()
  }

  clearFile() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ''
      if (typeof DataTransfer !== 'undefined') {
        try {
          const dataTransfer = new DataTransfer()
          this.fileInputTarget.files = dataTransfer.files
        } catch (error) {
          // DataTransfer constructor may not be supported; ignore.
        }
      }
    }
  }

  uncheckRemove() {
    if (this.hasRemoveCheckboxTarget) {
      this.removeCheckboxTarget.checked = false
    }
  }

  setRemoveCheckbox(boolean) {
    if (!this.hasMetaRowTarget) return

    if (boolean) {
      this.metaRowTarget.classList.remove('hidden')
    } else {
      this.metaRowTarget.classList.add('hidden')
    }
  }

  disableInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.classList.add('pointer-events-none', 'hidden')
      if (!this.fileInputTarget.dataset.originalTabindex) {
        this.fileInputTarget.dataset.originalTabindex = this.fileInputTarget.getAttribute('tabindex') || ''
      }
      this.fileInputTarget.setAttribute('tabindex', '-1')
      this.fileInputTarget.setAttribute('title', '')
      this.fileInputTarget.setAttribute('aria-label', '')
      this.fileInputTarget.setAttribute('data-balloon-visible', 'false')
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('pointer-events-none', 'cursor-not-allowed', 'opacity-80')
      this.dropzoneTarget.classList.remove('cursor-pointer')
    }
  }

  enableInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.classList.remove('pointer-events-none', 'hidden')
      const original = this.fileInputTarget.dataset.originalTabindex
      if (original === '') {
        this.fileInputTarget.removeAttribute('tabindex')
      } else if (original != null) {
        this.fileInputTarget.setAttribute('tabindex', original)
      }
      this.fileInputTarget.removeAttribute('title')
      this.fileInputTarget.removeAttribute('aria-label')
      this.fileInputTarget.removeAttribute('data-balloon-visible')
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('pointer-events-none', 'cursor-not-allowed', 'opacity-80')
      this.dropzoneTarget.classList.add('cursor-pointer')
    }
  }

  isDisabled() {
    return this.hasFileInputTarget && this.fileInputTarget.classList.contains('pointer-events-none')
  }
}
